import { useCallback } from 'react';
import { useTranslation } from 'next-i18next';

import { SpacePlanPaymentPriceInfo } from '@zep/types';

import {
  Currency,
  PlanProduct,
  PlansProduct,
  SubscribePlan,
} from '../../../../../types';
import { fCurrency } from '../../../../../utils';

/**
 *  선택한 플랜의 정보를 얻는 hook
 * */
export const useGetPlanInfo = ({
  currency,
  isAnnuallyPayment,
  customSubscriptionRequestPriceInfo,
}: {
  currency: Currency;
  isAnnuallyPayment: boolean;
  customSubscriptionRequestPriceInfo?: SpacePlanPaymentPriceInfo | null;
}) => {
  const { t } = useTranslation(['subscription']);

  return useCallback(
    ({
      plans,
      planType,
      maxCcu,
    }: {
      plans: PlansProduct;
      planType: Omit<SubscribePlan, 'free'>;
      maxCcu: number;
    }) => {
      const key = planType === 'pro' ? 'PRO' : 'BASIC';

      const findPlanProducts = plans[key]?.filter(({ subscriptionPeriod }) => {
        return isAnnuallyPayment
          ? subscriptionPeriod === 365
          : subscriptionPeriod === 30;
      });
      const firstPlan = Object.keys(plans)[0];

      const planProducts: PlanProduct[] =
        // @ts-ignore
        Object.keys(plans).length === 1 ? plans[firstPlan] : findPlanProducts;

      const planProduct = planProducts.find(
        ({ maxCcu: _maxCcu }) => _maxCcu === maxCcu,
      );

      // 없을경우 1번째 플랜 프로덕트 상품으로 대체한다
      const { ...rest } = planProduct || { ...planProducts[0] };
      const snapshotPriceInfo =
        rest.priceInfo ??
        (rest.type === 'CUSTOM_SUBSCRIPTION'
          ? customSubscriptionRequestPriceInfo
          : null);
      const finalPrice =
        snapshotPriceInfo?.finalPrice ??
        (rest.saleType ? rest.salePrice : rest.originalPrice);

      return {
        plan: findPlanProducts
          ? planType
          : (firstPlan.toLowerCase() as SubscribePlan),
        ...rest,
        isSales: !!rest.saleType,
        isAnnualSales: isAnnuallyPayment,
        finalPriceString: fCurrency(finalPrice, currency, {
          currencyString: t('subscription:currency.won'),
        }),
        finalPrice,
        priceInfo: snapshotPriceInfo,
      };
    },
    [currency, customSubscriptionRequestPriceInfo, isAnnuallyPayment, t],
  );
};
